import { useCallback, useState } from 'react';
import { useApiFetch } from '../api';
import type { GpuServer, GpuServerStartBody } from '../types/models/GpuServer';
import type { ApiResponseGpuServerStart } from '../types/api/ApiResponseGpuServer';
import { GPU_SERVERS_UNAVAILABLE_MESSAGE, jsendFieldErrors, readJsendBody } from './gpuServerResponse';

/** Shown when the start's outcome could not be read (the first request may still have started a server). */
export const GPU_SERVER_START_UNCONFIRMED_MESSAGE =
  'We could not confirm the start. Retry — the same request never starts a second server.';

export type GpuServerStartOutcome =
  /** Money may have moved: render the RETURNED server (after a replay it may differ from the inputs). */
  | { status: 'success'; server: GpuServer; replay: boolean }
  /** Refused before any debit: nothing was charged and no server exists for the key. */
  | { status: 'error'; message: string; fieldErrors: Record<string, string> }
  /** Network error, aborted request, non-JSON or malformed body: lock the steps, offer Retry (same key). */
  | { status: 'unconfirmed'; message: string };

export interface UseStartGpuServerReturn {
  /** Never throws. Don't wrap it in a timeout or an abort: the request is slow (5–35 s). */
  start: (body: GpuServerStartBody) => Promise<GpuServerStartOutcome>;
  loading: boolean;
}

/**
 * `POST /datacenters/gpu-servers` — start a GPU server, debiting its first hour from the prepaid
 * balance (docs/api-types/gpu-servers.md). It calls the upstream, so it takes 5–35 s.
 *
 * Idempotency is the caller's job: send the SAME `idempotency_key` on every retry of one wizard
 * session, so a retry after a lost response replays the first server instead of paying for a second.
 *
 * Outcome mapping (the backend always answers `success` once money has moved):
 * - `success` with a server → `success` (check `server.status`: `failed` = refused and refunded);
 * - JSend `error` → `error` with the verbatim message and its `data.<field>` map;
 * - the router's `fail` (route not deployed) → `error`: nothing can have run;
 * - anything else (a thrown fetch, a non-JSON body, a `success` missing its server) → `unconfirmed`.
 */
export const useStartGpuServer = (): UseStartGpuServerReturn => {
  const apiFetch = useApiFetch();
  const [loading, setLoading] = useState(false);

  const start = useCallback(
    async (payload: GpuServerStartBody): Promise<GpuServerStartOutcome> => {
      setLoading(true);
      try {
        const response = await apiFetch('/datacenters/gpu-servers', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        const body = await readJsendBody<ApiResponseGpuServerStart>(response);
        if (body?.status === 'success' && body.data?.server) {
          return { status: 'success', server: body.data.server, replay: body.data.replay === true };
        }
        if (body?.status === 'error') {
          return {
            status: 'error',
            message:
              typeof body.message === 'string' && body.message !== ''
                ? body.message
                : 'The server could not be started, please retry',
            fieldErrors: jsendFieldErrors(body),
          };
        }
        if (body?.status === 'fail') {
          return { status: 'error', message: GPU_SERVERS_UNAVAILABLE_MESSAGE, fieldErrors: {} };
        }
        return { status: 'unconfirmed', message: GPU_SERVER_START_UNCONFIRMED_MESSAGE };
      } catch {
        return { status: 'unconfirmed', message: GPU_SERVER_START_UNCONFIRMED_MESSAGE };
      } finally {
        setLoading(false);
      }
    },
    [apiFetch],
  );

  return { start, loading };
};

export default useStartGpuServer;
